<script setup>
import { ref } from "vue";
import Swal from "sweetalert2";
import AuthLayout from "@/components/layouts/AuthLayout.vue";
import DOMPurify from "dompurify";
import { useRouter } from "vue-router";

const router = useRouter();
const isLoading = ref(false);

const email = ref("");
const code = ref("");
const password = ref("");
const passwordConfirm = ref("");

const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;
  return DOMPurify.sanitize(input.trim());
};

const resetPassword = async (email, resetCode, password, passwordConfirmation) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email,
        reset_code: resetCode,
        password,
        password_confirmation: passwordConfirmation
      })
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      throw { response: { data } };
    }
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

const requestNewCode = async () => {
  if (!email.value || !email.value.includes("@")) {
    Swal.fire("Correo requerido", "Ingresa tu correo para solicitar un nuevo código", "error");
    return;
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ email: sanitizeInput(email.value.toLowerCase()) })
    });

    const data = await response.json();

    if (response.ok) {
      Swal.fire({
        icon: "success",
        title: "Nuevo código enviado",
        text: "Se ha enviado un nuevo código de verificación a tu correo electrónico",
        confirmButtonColor: "#99b642",
      });
    } else {
      throw { response: { data } };
    }
  } catch (error) {
    let errorMessage = "Error al enviar el nuevo código";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    Swal.fire("Error", errorMessage, "error");
  }
};

const onSubmit = async () => {
  if (!email.value || !code.value || !password.value || !passwordConfirm.value) {
    return Swal.fire("Campos vacíos", "Completa todos los campos", "error");
  }

  if (!email.value.includes("@")) {
    return Swal.fire("Correo inválido", "Ingresa un correo válido", "error");
  }

  if (code.value.length !== 6) {
    return Swal.fire("Código inválido", "El código debe tener exactamente 6 dígitos", "error");
  }

  if (password.value.length < 8) {
    return Swal.fire("Contraseña muy corta", "La contraseña debe tener al menos 8 caracteres", "error");
  }

  if (password.value !== passwordConfirm.value) {
    return Swal.fire("Contraseñas diferentes", "Las contraseñas no coinciden", "error");
  }

  isLoading.value = true;

  try {
    await resetPassword(
      sanitizeInput(email.value.toLowerCase()),
      sanitizeInput(code.value),
      password.value,
      passwordConfirm.value
    );

    Swal.fire({
      icon: "success",
      title: "¡Contraseña actualizada!",
      text: "Contraseña restablecida exitosamente. Por favor inicia sesión con tu nueva contraseña.",
      confirmButtonColor: "#99b642",
    }).then(() => {
      router.push("/login");
    });
  } catch (error) {
    let errorMessage = "Error al restablecer la contraseña. Intenta nuevamente.";
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.errors) {
      // Mostrar el primer error encontrado
      const firstError = Object.values(error.response.data.errors)[0];
      if (firstError && firstError[0]) {
        errorMessage = firstError[0];
      }
    }
    
    Swal.fire("Error", errorMessage, "error");
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <AuthLayout>
    <template #title>
      <h1 class="text-4xl font-extrabold text-white mb-2 drop-shadow-lg">
        Cambiar contraseña
      </h1>
    </template>

    <form @submit.prevent="onSubmit" class="space-y-6 max-w-md mx-auto w-full">
      <div>
        <label class="text-white block mb-2">Correo electrónico</label>
        <input
          v-model="email"
          type="email"
          placeholder="Tu correo electrónico"
          class="w-full px-4 py-2 rounded-md bg-white text-black shadow"
          required
        />
        <small class="text-gray-200 text-sm">Necesario para validar el código</small>
      </div>

      <div>
        <label class="text-white block mb-2">Código de verificación</label>
        <input
          v-model="code"
          type="text"
          placeholder="Código recibido de 6 dígitos"
          maxlength="6"
          pattern="[0-9]{6}"
          class="w-full px-4 py-2 rounded-md bg-white text-black shadow"
          required
        />
        <small class="text-gray-200 text-sm">Ingresa el código de 6 dígitos enviado a tu correo</small>
      </div>

      <div>
        <label class="text-white block mb-2">Nueva contraseña</label>
        <input
          v-model="password"
          type="password"
          placeholder="Nueva contraseña (mínimo 8 caracteres)"
          minlength="8"
          class="w-full px-4 py-2 rounded-md bg-white text-black shadow"
          required
        />
      </div>

      <div>
        <label class="text-white block mb-2">Confirmar contraseña</label>
        <input
          v-model="passwordConfirm"
          type="password"
          placeholder="Repite la nueva contraseña"
          minlength="8"
          class="w-full px-4 py-2 rounded-md bg-white text-black shadow"
          required
        />
      </div>

      <button
        type="submit"
        class="bg-[#99b642] hover:bg-[#88a534] text-white font-semibold py-2 px-4 rounded w-full"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="flex justify-center items-center">
          <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          Cambiando contraseña...
        </span>
        <span v-else>Cambiar contraseña</span>
      </button>

      <button
        type="button"
        class="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded w-full"
        @click="requestNewCode"
        :disabled="isLoading"
      >
        Solicitar nuevo código
      </button>
    </form>

    <template #footer-links>
      <p class="text-center text-white mt-6">
        ¿Ya tienes cuenta?
        <router-link
          to="/login"
          class="font-semibold underline hover:text-[#d4e4a9] transition-colors ml-1"
        >
          Inicia sesión
        </router-link>
      </p>
    </template>
  </AuthLayout>
</template>